import {useCurrentEventCounts} from '@src/modules/Events/hooks/useCurrentEventCounts'
import {LocationActivityFeed} from '@src/modules/Locations/components/LocationActivityFeed.tsx'
import {BoloList} from '@src/modules/Locations/components/ViewLocation/components/Bolo'
import {LocationEventsIndex} from '@src/modules/Locations/components/ViewLocation/components/Events'
import {SubLocationsList} from '@src/modules/Locations/components/ViewLocation/components/SubLocations'
import {LocationLogs} from '@src/modules/Locations/components/ViewLocation/components/LocationLogs'
import {PassDownList} from '@src/modules/Locations/components/ViewLocation/components/PassDown'
import {ReportsIndex} from '@src/modules/Locations/components/ViewLocation/components/Reports'
import {RecordsList} from '@src/modules/Records/shared/RecordsList'
import {LocationSidebarDetails} from '@src/modules/Locations/components/ViewLocation/components/LocationSidebarDetails.tsx'
import {LocationMobileHeader} from '@src/modules/Locations/components/ViewLocation/components/LocationMobileHeader'
import {
  Constants,
  Loading,
  Tabs,
  useContainerDimensionsContext,
} from '@therms/atalaya'
import {
  ErrorLoadingRetry,
  LocationContextProvider,
  LocationInfoTabs,
  RegionName,
  RPCDocs,
  RPCs,
  useCurrentUserRegions,
  useLocation,
  UserClientViewingLocation,
  useRPC,
  useRPCServerEventHandler,
  useTabsState,
} from '@therms/react-modules'
import classNames from 'classnames'
import {useMemo} from 'react'
import {LocationTasks} from '@src/modules/Locations/components/ViewLocation/components/LocationTasks'
import {LocationDetailsMapScreen} from '@src/modules/Locations/components/ViewLocation/components/LocationDetailsMap'

const TAB_KEYS = {
  ACTIVITY_FEED: 'activity-feed',
  BOLO: 'bolo',
  BRIEF: 'brief',
  EVENTS: 'events',
  INFO: 'info',
  LOG: 'log',
  MAP: 'map',
  PASS_DOWN: 'passdown',
  RECORDS: 'records',
  REPORTS: 'reports',
  SUB_LOCATIONS: 'sublocations',
  TASKS: 'tasks',
}

interface ViewLocationProps {
  locationId: string
}

function ViewLocation({locationId}: ViewLocationProps) {
  const {currentRegion} = useCurrentUserRegions()
  const [tab, setTab] = useTabsState(`tab-${locationId}`, `log`)
  const {data, loading, refresh} = useLocation({locationId})
  const {counts} = useCurrentEventCounts({locationId})

  const {data: activityData} = useRPC<
    RPCDocs.Locations.GetLocationActivitySummary.v1.Args,
    RPCDocs.Locations.GetLocationActivitySummary.v1.Data
  >(RPCs.Locations.GetLocationActivitySummary.v1, {
    locationIds: [locationId],
    nearbyRadiusMeters: 20,
  })

  const activitySummary = activityData?.activitySummaries?.[0]

  useRPCServerEventHandler('LocationUpdated', refresh)

  const {width: viewportWidth} = useContainerDimensionsContext()

  const isMobileLayout = viewportWidth < Constants.Breakpoint.Sm.width

  const showInfoTab = useMemo(() => {
    if (!data) return false

    return !!Object.values(data.location?.info || {}).length
  }, [data])

  if (!data && !loading) return <ErrorLoadingRetry onRetry={refresh} />
  if (!data) return <Loading overlay size="xxl" />

  const {location} = data

  return (
    <LocationContextProvider locationId={locationId}>
      <UserClientViewingLocation locationId={locationId} />

      <div
        className={classNames(
          {
            'flex flex-col': isMobileLayout,
            'grid grid-cols-[max-content_1fr]': !isMobileLayout,
          },
          'h-full',
        )}
      >
        {/* Region warning banner - spans full width on grid */}
        {currentRegion && currentRegion.id !== location.regionId && (
          <div
            className={classNames(
              'bg-caution-faded text-semantic p-xs flex flex-col items-center rounded',
              {
                'col-span-2': !isMobileLayout,
                'mx-xs mt-xs md:mx-base md:mt-base': isMobileLayout,
              },
            )}
          >
            <span className="text-sm italic">
              Location in a different Region - all information will not be
              displayed until you change Regions
            </span>
            <span className="font-bold">
              <RegionName regionId={location.regionId} />
            </span>
          </div>
        )}

        {/* Sidebar on desktop, header on mobile */}
        {!isMobileLayout ? (
          <div className="border-border bg-background w-64 shrink-0 overflow-y-auto border-r">
            <LocationSidebarDetails
              location={location}
              locationId={locationId}
              activitySummary={activitySummary}
            />
          </div>
        ) : (
          <LocationMobileHeader
            location={location}
            locationId={locationId}
            activitySummary={activitySummary}
          />
        )}

        {/* Main content area */}
        <div className="flex flex-col overflow-hidden">
          <div>
            <Tabs currentKey={tab} onTabClick={setTab}>
              {/* <Tabs.Tab tabKey="brief" title="Brief" /> */}
              <Tabs.Tab tabKey="activity-feed" title="Activity Feed" />

              <Tabs.Tab
                tabKey={TAB_KEYS.EVENTS}
                title="Events"
                notifications={counts.active + counts.pending}
              />

              {location?.settings?.modules?.log && (
                <Tabs.Tab tabKey={TAB_KEYS.LOG} title="Log" />
              )}

              {/*<Tabs.Tab tabKey={TAB_KEYS.TASKS} title="Tasks" />*/}

              <Tabs.Tab
                tabKey={TAB_KEYS.BOLO}
                title="BOLO"
                notifications={location?.bolos?.count}
              />

              <Tabs.Tab
                tabKey={TAB_KEYS.PASS_DOWN}
                title="Pass Down"
                notifications={location?.passDowns}
              />

              {location.geo?.coordinates && (
                <Tabs.Tab tabKey={TAB_KEYS.MAP} title="Map" />
              )}

              <Tabs.Tab tabKey={TAB_KEYS.RECORDS} title="Records" />

              <Tabs.Tab tabKey={TAB_KEYS.REPORTS} title="Reports" />

              {!!location.info.subLocations && (
                <Tabs.Tab
                  tabKey={TAB_KEYS.SUB_LOCATIONS}
                  title="Sub-Locations"
                />
              )}

              {showInfoTab && <Tabs.Tab tabKey={TAB_KEYS.INFO} title="Info" />}
            </Tabs>
          </div>

          <div className="px-xs grow overflow-y-auto">
            {/* {tab === TAB_KEYS.BRIEF && <BriefList />} */}
            {tab === TAB_KEYS.ACTIVITY_FEED && <LocationActivityFeed />}
            {tab === TAB_KEYS.EVENTS && (
              <LocationEventsIndex hideNewEventButton={!location.active} />
            )}
            {location?.settings?.modules?.log && tab === TAB_KEYS.LOG && (
              <LocationLogs />
            )}
            {tab === TAB_KEYS.TASKS && <LocationTasks />}
            {tab === TAB_KEYS.BOLO && <BoloList />}
            {tab === TAB_KEYS.PASS_DOWN && <PassDownList />}
            {location.geo?.coordinates && tab === TAB_KEYS.MAP && (
              <LocationDetailsMapScreen />
            )}
            {tab === TAB_KEYS.RECORDS && (
              <RecordsList
                hideNewRecordButton={!location.active}
                locationId={locationId}
                showRecentRecords={false}
              />
            )}
            {tab === TAB_KEYS.REPORTS && (
              <ReportsIndex locationId={locationId} />
            )}

            {!!location.info.subLocations && tab === TAB_KEYS.SUB_LOCATIONS && (
              <SubLocationsList />
            )}

            {showInfoTab && tab === TAB_KEYS.INFO && <LocationInfoTabs />}
          </div>
        </div>
      </div>
    </LocationContextProvider>
  )
}

// used for code-splitting
// eslint-disable-next-line import-x/no-default-export
export default ViewLocation
export {ViewLocation}
